/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/camelcase */
import {
  Button as MaterialButton,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import InputIcon from '@material-ui/icons/Input';
import clsx from 'clsx';
import PageTitle from 'components/PageTitle';
import RefreshButton from 'components/RefreshButton';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import Permission from 'components/ValidPermission';
import i18n from 'i18n';
import {
  CompanyData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import { modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import { getAllPartnersRequest } from 'modules/partners/actions';
import {
  ListPartnerDataProps,
  PartnerFields,
} from 'modules/partners/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import { formatDateValue } from 'utils/dateFormat';
import { manipulateLocalStorage, parseNameLdap } from 'utils/helpers';
import { inAdminView, inOperatorView, inSuperAdminView } from 'utils/role';
import { useTableFilters } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import { LOCAL_STORAGE_CUSTOM_KEY } from '../CustomPartnerFront/FormCustom';
import styles from './styles';
import FormClonePartner from './FormClonePartner';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 25,
  search: '',
};

const requiredActionsPermissions: Array<keyof PermissionsData> = [
  'create_new_partner',
  'edit_partner_details',
  'edit_user_theme_config',
  'list_partner_details',
  'list_partner_servers',
  'list_partner_instances',
];

interface Props {
  isLoading: boolean;
  partnerList: ListPartnerDataProps;
  companySelected: CompanyData | null;
  role?: RolesEnum;
  permissions?: PermissionsData;
  getAllPartnersRequest: (props) => void;
  modalOpen: (props: ModalFields) => void;
}

const PartnersList = ({
  isLoading,
  partnerList,
  role,
  companySelected,
  permissions,
  getAllPartnersRequest,
  modalOpen,
}: Props) => {
  const classes = useStyles();

  const {
    itemOrder,
    query,
    searchValue,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  const history = useHistory();
  const [refresh, setRefresh] = React.useState(false);

  const headers = [
    {
      key: 'nome',
      i18n: 'PARTNER_NAME_KEY',
      order: true,
    },
    {
      key: 'alias',
      i18n: 'ALIAS_KEY',
      order: true,
      hidden: permissions?.can_view_all_resources,
    },
    {
      key: 'ldap_type',
      i18n: 'LDAP',
      order: true,
      centerTitle: true,
      hidden: permissions?.can_view_all_resources,
    },
    {
      key: 'flag_new_front',
      i18n: 'USE_FRONT_KEY',
      order: true,
      centerTitle: true,
      hidden: permissions?.can_view_all_resources,
    },
    {
      key: 'status',
      i18n: 'PARTNER_PAGE.CUSTOM_STATUS_PUBLICATION_KEY',
      order: false,
      centerTitle: true,
    },
    {
      key: 'user_last_access',
      i18n: 'LAST_ACCESS_SINGULAR_KEY',
      order: true,
      centerTitle: true,
    },
  ].filter(header => !header.hidden);

  const getPartners = (refresh = false) => {
    if (companySelected || permissions?.can_view_all_resources) {
      getAllPartnersRequest({
        query,
        company_uuid: permissions?.can_view_all_resources
          ? ''
          : companySelected?.id,
      });

      setRefresh(refresh);
    }
  };

  React.useEffect(() => {
    getPartners();
  }, [getAllPartnersRequest, query, companySelected]);

  const handleClick = (item: PartnerFields) => {
    if (permissions?.edit_partner_details) {
      history.push(`partners/${item?.partner_uuid}/configuration`);
    }
  };

  const openFormClonePartner = (partner: PartnerFields) => {
    modalOpen({
      title: i18n.t('PARTNER_PAGE.CLONE_PARTNER_KEY'),
      content: <FormClonePartner partner={partner} query={query} />,
      noBorder: true,
      maxWidth: 'md',
      fullWidth: true,
    });
  };

  return (
    <Grid container>
      <Grid
        container
        direction="row"
        alignItems="center"
        justify="space-between"
      >
        <PageTitle
          title={i18n.t('PARTNER_PAGE.LIST_PARTNER_KEY')}
          subtitle={i18n.t('PARTNER_PAGE.LIST_PARTNER_AVAILABLE_KEY')}
        />

        <Grid item direction="row" xs={3} className={classes.item}>
          <RefreshButton
            id="btn-refresh"
            onClick={() => getPartners(true)}
            animate={isLoading}
            disabled={isLoading}
          />

          <Permission value="create_new_partner">
            <MaterialButton
              id="new-partner"
              variant="contained"
              size="large"
              className={clsx({
                [classes.addButtonGreen]: inAdminView(role, companySelected),
                [classes.addButtonGray]: inSuperAdminView(
                  role,
                  companySelected
                ),
                [classes.addButtonBlue]: inOperatorView(role, companySelected),
                [classes.addButtonYellow]: companySelected,
              })}
              startIcon={<AddIcon className={classes.addButtonIcon} />}
              component={Link}
              to="/partners/new"
            >
              {i18n.t('PARTNER_KEY')}
            </MaterialButton>
          </Permission>
        </Grid>
      </Grid>

      <Grid container>
        <SearchBar
          id="search-partners"
          isLoading={isLoading}
          searchValue={searchValue}
          placeholder={i18n.t('PARTNER_PAGE.SEARCH_PARTNER_KEY')}
          handleSearch={handleSearch}
          setSearchValue={setSearchValue}
        />
        <TableItems
          header={
            <>
              <Permission value="edit_partner_details">
                <TableCell className={classes.accessButtonHeader} centerTitle>
                  {i18n.t('ACCESS_KEY')}
                </TableCell>
              </Permission>
              {headers.map(item => (
                <TableCell
                  centerTitle={item?.centerTitle}
                  key={item?.key}
                  order={item?.order}
                  markOderItem={itemOrder === item?.key}
                  handleOrder={() =>
                    item?.order ? handleOrder(item?.key) : null
                  }
                >
                  {i18n.t(item?.i18n)}
                </TableCell>
              ))}
              <Permission value={requiredActionsPermissions}>
                <TableCell centerTitle>{i18n.t('ACTIONS_KEY')}</TableCell>
              </Permission>
            </>
          }
          renderItems={(item: PartnerFields, index) => (
            <TableRow
              className={clsx(classes.summaryRowTable, {
                [classes.envRowDirect]:
                  !!permissions && permissions.edit_partner_details,
              })}
              key={item?.partner_uuid}
            >
              <Permission value="edit_partner_details">
                <TableCell
                  ellipsis
                  centerTitle
                  className={classes.centerFields}
                  onClick={() => {
                    handleClick(item);
                  }}
                >
                  <IconButton>
                    <InputIcon className={classes.accessButton} />
                  </IconButton>
                </TableCell>
              </Permission>
              <TableCell
                onClick={() => {
                  handleClick(item);
                }}
                className={classes.textField}
              >
                {item?.name}
              </TableCell>
              {!permissions?.can_view_all_resources && (
                <TableCell
                  onClick={() => {
                    handleClick(item);
                  }}
                >
                  {item?.alias || 'N/A'}
                </TableCell>
              )}
              {!permissions?.can_view_all_resources && (
                <TableCell
                  onClick={() => {
                    handleClick(item);
                  }}
                  className={classes.centerLdap}
                >
                  <Grid container justify="center">
                    {parseNameLdap(item?.active_directory.ldap_type)}
                  </Grid>
                </TableCell>
              )}
              {!permissions?.can_view_all_resources && (
                <TableCell
                  onClick={() => {
                    handleClick(item);
                  }}
                  className={classes.toUppercaseClass}
                >
                  <Grid container justify="center">
                    {item?.frontend.enable_new_interface
                      ? i18n.t('YES_KEY')
                      : i18n.t('NO_KEY')}
                  </Grid>
                </TableCell>
              )}
              <TableCell
                onClick={() => {
                  handleClick(item);
                }}
                centerTitle
                className={classes.statusPublication}
              >
                {i18n.t(
                  manipulateLocalStorage(LOCAL_STORAGE_CUSTOM_KEY)
                    ? 'SERVER_PENDING_KEY'
                    : 'PARTNER_PAGE.CUSTOM_STATUS_NOT_DEFINE_KEY'
                )}
              </TableCell>
              <TableCell
                onClick={() => {
                  handleClick(item);
                }}
                centerTitle
                textAlignCenter
              >
                {formatDateValue(item?.user_last_access)}
              </TableCell>
              <Permission value={requiredActionsPermissions}>
                <TableCell textAlignCenter>
                  <MenuActions id={`btn-options-partners-${index}`}>
                    <Permission value="edit_partner_details">
                      <MenuActionItem
                        id="btn-partner-configuration"
                        component={Link}
                        to={`partners/${item?.partner_uuid}/configuration`}
                      >
                        <Typography className={classes.menuOptions}>
                          {i18n.t('CONFIG_KEY')}
                        </Typography>
                      </MenuActionItem>
                    </Permission>
                    <Permission value="list_partner_servers">
                      <MenuActionItem
                        id="btn-partner-servers"
                        component={Link}
                        to={`partners/${item?.partner_uuid}/servers`}
                      >
                        <Typography className={classes.menuOptions}>
                          {i18n.t('SERVERS_KEY')}
                        </Typography>
                      </MenuActionItem>
                    </Permission>
                    {item?.show_instances_list && (
                      <Permission value="list_partner_instances">
                        <MenuActionItem
                          id="btn-instances"
                          component={Link}
                          to={`partners/${item?.partner_uuid}/instances`}
                        >
                          <Typography className={classes.menuOptions}>
                            {i18n.t('INSTANCES_KEY')}
                          </Typography>
                        </MenuActionItem>
                      </Permission>
                    )}
                    <Permission value="edit_user_theme_config">
                      <MenuActionItem
                        id="btn-custom-front"
                        component={Link}
                        to={`partners/${item?.partner_uuid}/custom-front`}
                      >
                        <Typography className={classes.menuOptions}>
                          {i18n.t('GROUPS_PAGE.FRONTEND_CUSTOMIZATION_KEY')}
                        </Typography>
                      </MenuActionItem>
                    </Permission>
                    <Permission value="create_new_partner">
                      <MenuActionItem
                        id="btn-clone-partner"
                        onClick={() => openFormClonePartner(item)}
                      >
                        <Typography className={classes.menuOptions}>
                          {i18n.t('CLONE_KEY')}
                        </Typography>
                      </MenuActionItem>
                    </Permission>
                  </MenuActions>
                </TableCell>
              </Permission>
            </TableRow>
          )}
          data={partnerList}
          isLoading={!refresh && isLoading}
          onChangePage={handlePageChange}
          onChangeLimit={handleLimitChange}
          notFoundText={i18n.t('PARTNER_PAGE.NONE_PARTNER_KEY')}
          alertText={
            !companySelected && !permissions?.can_view_all_resources
              ? i18n.t('TO_LIST_SELECT_COMPANY_KEY')
              : null
          }
        />
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { companySelected, data },
  partners: { isLoading, partnerList },
}: ApplicationState) => ({
  isLoading,
  partnerList,
  companySelected,
  role: data?.role,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  getAllPartnersRequest,
  modalOpen,
})(PartnersList);
